// API service for heatmap data
import { API_BASE_URL } from '../config/api.config';
import { getRuntimeConfig } from '../utils/runtime-config';
export interface HeatmapPoint {
  id: string;
  coordinates: [number, number];
  value: number;
  type: 'jobs' | 'salaries' | 'companies';
  metadata?: {
    jobCount?: number;
    avgSalary?: number;
    companyCount?: number;
    zoom?: number;
    gridSize?: number;
  };
}

export interface ViewportBounds {
  north: number;
  south: number;
  east: number;
  west: number;
  zoom: number;
}

export interface HeatmapResponse {
  data: HeatmapPoint[];
  cached: boolean;
  bounds: ViewportBounds;
  zoom: number;
  dataType: string;
  count: number;
}

export interface HeatmapStats {
  jobs: {
    total: number;
    average: number;
    max: number;
  };
  salaries: {
    average: number;
    max: number;
    min: number;
  };
  companies: {
    total: number;
    unique: number;
    average: number;
  };
  viewport: ViewportBounds;
}

class HeatmapApiService {
  private baseUrl: string;
  
  constructor() {
    this.baseUrl = API_BASE_URL;
  }
  
  async getHeatmapData(
    bounds: ViewportBounds, 
    dataType: 'jobs' | 'salaries' | 'companies'
  ): Promise<HeatmapResponse> {
    // Skip API call if using mock data
    if (getRuntimeConfig('REACT_APP_USE_MOCK_DATA') === 'true') {
      return this.getMockHeatmapData(bounds, dataType);
    }
    
    try {
      const params = new URLSearchParams({
        north: bounds.north.toString(),
        south: bounds.south.toString(),
        east: bounds.east.toString(),
        west: bounds.west.toString(),
        zoom: bounds.zoom.toString(),
        dataType
      });
      
      const response = await fetch(`${this.baseUrl}/api/heatmap/data?${params}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch heatmap data: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.warn('API unavailable, using mock heatmap data');
      // Return mock data when API is unavailable
      return this.getMockHeatmapData(bounds, dataType);
    }
  }
  
  private getMockHeatmapData(
    bounds: ViewportBounds, 
    dataType: 'jobs' | 'salaries' | 'companies'
  ): HeatmapResponse {
    const mockPoints: HeatmapPoint[] = [];
    
    // Generate some mock points based on major cities
    const cities = [
      { name: 'New York', coords: [-74.006, 40.7128], avgSalary: 125000, jobs: 50000, companies: 5000 },
      { name: 'London', coords: [-0.1276, 51.5074], avgSalary: 95000, jobs: 45000, companies: 4500 },
      { name: 'San Francisco', coords: [-122.4194, 37.7749], avgSalary: 155000, jobs: 35000, companies: 3500 },
      { name: 'Berlin', coords: [13.405, 52.52], avgSalary: 75000, jobs: 25000, companies: 2500 },
      { name: 'Paris', coords: [2.3522, 48.8566], avgSalary: 85000, jobs: 30000, companies: 3000 },
      { name: 'Tokyo', coords: [139.6503, 35.6762], avgSalary: 90000, jobs: 60000, companies: 6000 },
      { name: 'Sydney', coords: [151.2093, -33.8688], avgSalary: 95000, jobs: 20000, companies: 2000 },
    ];
    
    // Filter cities within bounds
    const visibleCities = cities.filter(city => {
      const [lng, lat] = city.coords;
      return lat >= bounds.south && lat <= bounds.north && 
             lng >= bounds.west && lng <= bounds.east;
    });
    
    // Generate grid points around visible cities
    visibleCities.forEach(city => {
      const gridSize = Math.max(0.5, 5 / Math.pow(2, bounds.zoom - 2));
      for (let i = -2; i <= 2; i++) {
        for (let j = -2; j <= 2; j++) {
          const distance = Math.sqrt(i * i + j * j);
          const factor = Math.max(0.2, 1 - distance * 0.2);
          
          let value = 0;
          let metadata: any = { zoom: bounds.zoom, gridSize };
          
          switch (dataType) {
            case 'salaries':
              value = city.avgSalary * factor * (0.8 + Math.random() * 0.4);
              metadata.avgSalary = value;
              break;
            case 'jobs':
              value = city.jobs * factor * (0.8 + Math.random() * 0.4);
              metadata.jobCount = value;
              break;
            case 'companies':
              value = city.companies * factor * (0.8 + Math.random() * 0.4);
              metadata.companyCount = value;
              break;
          }
          
          mockPoints.push({
            id: `${city.name}-${i}-${j}`,
            coordinates: [
              city.coords[0] + i * gridSize,
              city.coords[1] + j * gridSize
            ],
            value,
            type: dataType,
            metadata
          });
        }
      }
    });
    
    return {
      data: mockPoints,
      cached: false,
      bounds,
      zoom: bounds.zoom,
      dataType,
      count: mockPoints.length
    };
  }
  
  async getViewportStats(bounds: Omit<ViewportBounds, 'zoom'>): Promise<HeatmapStats> {
    const params = new URLSearchParams({
      north: bounds.north.toString(),
      south: bounds.south.toString(),
      east: bounds.east.toString(),
      west: bounds.west.toString()
    });
    
    const response = await fetch(`${this.baseUrl}/api/heatmap/stats?${params}`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch viewport stats: ${response.statusText}`);
    }
    
    return await response.json();
  }
  
  async getDataTypes() {
    const response = await fetch(`${this.baseUrl}/api/heatmap/types`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch data types: ${response.statusText}`);
    }
    
    return await response.json();
  }
  
  async checkHealth() {
    try {
      const response = await fetch(`${this.baseUrl}/health`);
      return await response.json();
    } catch (error) {
      console.error('Health check failed:', error);
      return { 
        status: 'unhealthy', 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }
}

export const heatmapApi = new HeatmapApiService();
export default heatmapApi;